import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Card,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { technicalNoteSettingsService } from '../services/api';

interface TechnicalNoteSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const TechnicalNoteSettingsDialog: React.FC<TechnicalNoteSettingsDialogProps> = ({ open, onClose }) => {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [newType, setNewType] = useState({ name: '', name_en: '', description: '' });
  const [editingType, setEditingType] = useState<any>(null);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await technicalNoteSettingsService.getSettings();
      setSettings(response.data.data);
    } catch (error) {
      console.error('Error loading technical note settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const handleCreateType = async () => {
    try {
      await technicalNoteSettingsService.createType(newType);
      setNewType({ name: '', name_en: '', description: '' });
      loadSettings();
    } catch (error) {
      console.error('Error creating technical note type:', error);
    }
  };

  const handleUpdateType = async () => {
    try {
      await technicalNoteSettingsService.updateType(editingType.id, editingType);
      setEditingType(null);
      loadSettings();
    } catch (error) {
      console.error('Error updating technical note type:', error);
    }
  };

  const handleDeleteType = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce type ?')) {
      try {
        await technicalNoteSettingsService.deleteType(id);
        loadSettings();
      } catch (error) {
        console.error('Error deleting technical note type:', error);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Paramètres des types de notes techniques</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Types de notes techniques</Typography>
            
            {/* Client Types */}
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              Vos types de notes techniques
            </Typography>
            <Stack spacing={1} sx={{ mb: 3 }}>
              {settings?.types?.map((type: any) => (
                <Box key={type.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={type.name} />
                  <IconButton size="small" onClick={() => setEditingType(type)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteType(type.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>

            {/* Add New Type */}
            <Card sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Ajouter un nouveau type</Typography>
              <Stack spacing={2}>
                <TextField
                  label="Nom du type"
                  value={newType.name}
                  onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Nom en anglais (optionnel)"
                  value={newType.name_en}
                  onChange={(e) => setNewType({ ...newType, name_en: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Description (optionnel)"
                  value={newType.description}
                  onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
                <Button variant="contained" onClick={handleCreateType} disabled={!newType.name}>
                  Ajouter le type
                </Button>
              </Stack>
            </Card>

            {/* Edit Type Dialog */}
            {editingType && (
              <Card sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>Modifier le type</Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Nom du type"
                    value={editingType.name}
                    onChange={(e) => setEditingType({ ...editingType, name: e.target.value })}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Nom en anglais"
                    value={editingType.name_en}
                    onChange={(e) => setEditingType({ ...editingType, name_en: e.target.value })}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Description"
                    value={editingType.description}
                    onChange={(e) => setEditingType({ ...editingType, description: e.target.value })}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button onClick={handleUpdateType} variant="contained" size="small">
                      Sauvegarder
                    </Button>
                    <Button onClick={() => setEditingType(null)} size="small">
                      Annuler
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TechnicalNoteSettingsDialog;
